import { Avatar, Form, FormItem, Heading, Select, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import styled from 'styled-components'
import { Modal, Form as AntdForm } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { getRequiredRule } from 'screens/Rostering/utils'
import { DateAndTimeRowPicker } from 'screens/Rostering/components'
import { useParams } from 'react-router-dom'
import useUser from 'hooks/useUser/useUser'
import { showToast } from 'common/utils'
// import dayjs from 'dayjs'
import { handleOnFinishFailed } from 'screens/Rostering/formComponents/utils'
import { Absence, User } from '@lmc/ts-common'
import { useAbsenceCreate } from 'screens/Rostering/hooks/useAbsenceCreate'
import { useAbsenceTypesList } from 'screens/Rostering/hooks/useAbsenceTypesList'

type AddIndividualAbsenceModalProps = {
  isModalVisible: boolean
  setIsModalVisible: (isVisible: boolean) => void
  all: boolean
  users: User[]
  absences?: Absence[]
}

const AddIndividualAbsenceModal = ({
  isModalVisible,
  setIsModalVisible,
  all,
  users,
}: // absences,
AddIndividualAbsenceModalProps) => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const { absenceTypes } = useAbsenceTypesList()
  const { id } = useParams()
  const { user } = useUser({ id, includes: ['rostering'] })

  const { createAbsence, isCreateLoading } = useAbsenceCreate({
    onCreateSuccess: () => {
      showToast({ title: t('ABSENCE_ADDED') })
      setIsModalVisible(false)
      form.resetFields()
    },
  })

  const onCancelClick = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const absenceTypesOptions = absenceTypes
    ?.filter((absenceType) => absenceType.isDeleted !== true)
    .map((absenceType) => ({
      value: absenceType.id,
      label: absenceType.name,
    }))

  const usersOptions = users?.map((user) => ({
    label: `${user.name.first} ${user.name.last}`,
    value: user.id,
    image: <Avatar url={user.picture as unknown as string} size={20} />,
  }))

  const handleOnFinish = async (values: any) => {
    const endDateValue = values['endDate.date']
    const endTimeValue = values['endDate.time']
    const endDate = endDateValue.format('YYYY-MM-DD')
    const endTime = endTimeValue.format('HH:mm:ss')
    const endDateWithTimezone = `${endDate}T${endTime}Z`

    const startDateValue = values['startDate.date']
    const startTimeValue = values['startDate.time']
    const startDate = startDateValue.format('YYYY-MM-DD')
    const startTime = startTimeValue.format('HH:mm:ss')
    const startDateWithTimezone = `${startDate}T${startTime}Z`

    const userId = all ? values.userId : user?.id

    try {
      await createAbsence({
        absenceType: values.absenceType,
        userId,
        startDate: startDateWithTimezone,
        endDate: endDateWithTimezone,
        absenceNotes: values?.absenceNotes,
        instancesUnallocated: 0,
      })
    } catch (e: any) {
      showToast({
        title: e?.message,
        type: 'error',
      })
    }
  }

  return (
    <Modal
      closable={false}
      open={isModalVisible}
      footer={[
        <ModalFooter>
          <Button onClick={onCancelClick}>
            <CloseOutlined />
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            isLoading={isCreateLoading}
          >
            {t('SAVE')}
          </Button>
        </ModalFooter>,
      ]}
    >
      <Wrapper>
        <TitleContainer>
          <Title level={3}>{t('ADD_ABSENCE')}</Title>
          <Divider />
        </TitleContainer>
        <Form
          form={form}
          onFinish={handleOnFinish}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
        >
          {all ? (
            <FormItem
              label={<Text>{t('TEAM_MEMBER')}</Text>}
              rules={[getRequiredRule(t('TEAM_MEMBER'))]}
              short
              name="userId"
            >
              <Select options={usersOptions} />
            </FormItem>
          ) : (
            <FormItem
              label={<Text>{t('TEAM_MEMBER')}</Text>}
              rules={[getRequiredRule(t('TEAM_MEMBER'))]}
              short
            >
              <Select
                disabled
                value={`${user?.name.first} ${user?.name.last}`}
              />
            </FormItem>
          )}
          <FormItem
            name="absenceType"
            label={<Text>{t('ABSENCE_TYPE')}</Text>}
            rules={[getRequiredRule(t('ABSENCE_TYPE'))]}
            short
          >
            <Select options={absenceTypesOptions} />
          </FormItem>
          <DateAndTimeRowPicker
            label={<Text>{t('START_DATE')}</Text>}
            name="startDate"
            required
            short
            form={form}
          />
          <DateAndTimeRowPicker
            label={<Text>{t('END_DATE')}</Text>}
            name="endDate"
            required
            short
            form={form}
          />
          {/* This should be shown based on start and end dates */}
          <FormItem
            noStyle
            dependencies={[
              'startDate.date',
              'endDate.date',
              'startDate.time',
              'endDate.time',
            ]}
          >
            {/* {({ getFieldValue }) => {
              const startDate =
                getFieldValue('startDate.date') &&
                getFieldValue('startDate.time')
              const endDate =
                getFieldValue('endDate.date') && getFieldValue('endDate.time')

              // Logic here for calculating number of shifts, this will be moved
              const numberOfShifts = 15

              if (!startDate || !endDate) return null
              return (
                <InfoContainer>
                  <GreyText weight="regular">
                    John Johnson is scheduled for{' '}
                    <GreyText weight="bold">{numberOfShifts} shifts</GreyText>{' '}
                    within the specified timeframe. Approving this leave request
                    will require reassigning these shifts to other team members.
                  </GreyText>
                </InfoContainer>
              )
            }} */}
          </FormItem>
          <FormItem
            name="absenceNotes"
            label={<Text>{t('ADDITIONAL_NOTES')}</Text>}
            short
          >
            <TextArea
              autoSize={{
                minRows: 2,
              }}
              showCount
              maxLength={200}
            />
          </FormItem>
          <Divider />
        </Form>
      </Wrapper>
    </Modal>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`

// const GreyText = styled(Text)`
//   color: ${(props) => props.theme.color.neutral.color4};
// `

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// const InfoContainer = styled.div`
//   margin-left: 160px;
// `

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

export default AddIndividualAbsenceModal
